import { useId, useRef, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangleIcon, LockIcon } from 'lucide-react';
import { dayLong, today } from '../../core/format/dates';
import { salonDayFor } from '../../core/rules/salonDays';
import { Panel } from '../ui/Panel';
import { NumberInput } from '../ui/NumberInput';
import { AutoTextarea } from '../ui/AutoTextarea';
import { useSubmit } from '../../hooks/useSubmit';
import { closeKassaDay } from '../adminApi';
import { newRequestId } from '../../core/api/http';
import { CLOSE_WORD, closeProblem, closeWarnings, isCloseWord } from '../../core/rules/kassa';
import { btnPrimary, btnSecondary, inputCls } from '../ui/styles';
import { money } from '../../core/format/currency';
import { clock, refundMethodLabel } from './kassaLabels';
import { printKuurCreditNote } from './kuurCreditNote';
import { printReturnCreditNote } from './returnCreditNote';
import { useAdmin } from '../AdminContext';
import { closedByLabel } from '../../core/rules/staffAccess';
import type { KassaDay, KuurStop, SaleReturn } from '../../core/types/kassa';
type Props = {
  data: KassaDay;
  onClosed: (day: KassaDay) => void;
  /** Collega: geen dagtotalen en blind tellen (core/rules/staffAccess). */
  totalsHidden?: boolean;
};

/** Het dagoverzicht: wat er binnenkwam per methode, en de afsluiting met de telling van de kas. */
export function DaySummary({
  data,
  onClosed,
  totalsHidden = false
}: Props) {
  const s = data.summary;
  const canClose = !data.closed;
  const count = s.sales === 0 ? 'Nog geen bonnen.' : `${s.sales} ${s.sales === 1 ? 'bon' : 'bonnen'}${s.corrected ? ` · ${s.corrected} gecorrigeerd` : ''}`;
  if (totalsHidden) {
    return <Panel title="Dag afsluiten" description={`Jouw bonnen vandaag: ${count.toLowerCase()}`} data-id="element-2301" data-mp="3jmzcj2wrwkdy">
        {data.closed ? <p className="flex items-center gap-2 text-[13px] text-ink" data-id="element-2302" data-mp="12xsruqlfqgqm">
            <LockIcon className="h-4 w-4 text-muted" strokeWidth={1.75} aria-hidden="true" data-id="element-2303" data-mp="1306jsqktjcty" />
            Afgesloten{closedByLabel(data.closed)} om {clock(data.closed.closedAt)} · geteld {money(data.closed.countedCashCents)}
          </p> : canClose && <CloseForm data={data} onClosed={onClosed} blind data-id="element-2304" data-mp="jcs30j71xg3" />}
      </Panel>;
  }
  return <Panel title="Dagoverzicht" description={count} data-id="element-2305" data-mp="5kimd2si5srq">
      <p className="font-display text-[2.2rem] font-light leading-none tabular-nums text-ink" data-id="element-2306" data-mp="9m47fbf6xdd0">{money(s.totalCents)}</p>
      <p className="mt-1 text-[12px] text-muted" data-id="element-2307" data-mp="391sgxh88psgf">Totaal incl. btw</p>

      <dl className="mt-5 space-y-1.5 border-t border-line pt-4 text-[14px]" data-id="element-2308" data-mp="3dzkiz42lmuyc">
        <Row label="Pin" value={s.byMethod.pin} data-id="element-2309" data-mp="3dxg4xtazkfkc" />
        <Row label="Contant" value={s.byMethod.contant} data-id="element-2310" data-mp="3i0cte0n7j5w7" />
        <Row label="Cadeaubon" value={s.byMethod.cadeaubon} data-id="element-2311" data-mp="3ms49fh8qlt83" />
        {(s.byMethod.tegoed ?? 0) > 0 && <Row label="Klanttegoed" value={s.byMethod.tegoed} data-id="element-2312" data-mp="3mtytktc6phb4" />}
      </dl>
      {s.voidRefunds && s.voidRefunds.pin + s.voidRefunds.contant > 0 && <p className="mt-3 text-[12px] text-muted" data-id="element-2313" data-mp="2uas0aoxcas97">
          Teruggegeven bij gecorrigeerde bonnen:{' '}
          {[s.voidRefunds.pin > 0 ? `${money(s.voidRefunds.pin)} pin` : '', s.voidRefunds.contant > 0 ? `${money(s.voidRefunds.contant)} contant` : ''].filter(Boolean).join(' · ')}. Die bonnen
          tellen hierboven al niet mee; op de pinautomaat staat de terugboeking wel.
        </p>}

      {(data.kuurRefunds?.length ?? 0) > 0 && <KuurRefunds refunds={data.kuurRefunds ?? []} data-id="element-2314" data-mp="28uvpaflxrf7w" />}
      {(data.returns?.length ?? 0) > 0 && <Returns returns={data.returns ?? []} data-id="element-2315" data-mp="23vj5y4u9wqa2" />}

      <dl className="mt-4 space-y-1.5 border-t border-line pt-4 text-[13px]" data-id="element-2316" data-mp="1z2hktf3h3ui1">
        <Row label="Behandelingen" value={s.byKind.treatments} small data-id="element-2317" data-mp="1u3dcsrsac2ta" />
        <Row label="Producten" value={s.byKind.products} small data-id="element-2318" data-mp="1u1k83i18fsnx" />
        <Row label="Kuren vooruit" value={s.byKind.kuren} small data-id="element-2319" data-mp="1q3cnud7k897z" />
        {(s.byKind.giftCards ?? 0) > 0 && <Row label="Cadeaubonnen verkocht (geen omzet)" value={s.byKind.giftCards} small data-id="element-2320" data-mp="1l1ncewgfdtjd" />}
        {s.vat.map(v => <Row key={v.vatRate} label={`Waarvan btw ${v.vatRate}%`} value={v.vatCents} small data-id="element-2321" data-mp="1g4nex9xn5z3v" />)}
      </dl>

      <div className="mt-5 border-t border-line pt-4" data-id="element-2322" data-mp="3g8hb9zwqme9l">
        {data.closed ? <div className="space-y-1 text-[13px]" data-id="element-2323" data-mp="3l4eovygm96io">
            <p className="flex items-center gap-2 text-ink" data-id="element-2324" data-mp="36gtm65nnl9ba">
              <LockIcon className="h-4 w-4 text-muted" strokeWidth={1.75} aria-hidden="true" data-id="element-2325" data-mp="36eh9o80nzr5m" />
              Afgesloten{closedByLabel(data.closed)} om {clock(data.closed.closedAt)}
            </p>
            <p className="text-muted" data-id="element-2326" data-mp="2xhq2azgnwpvq">
              Contant verwacht {money(data.closed.expectedCashCents)} · geteld {money(data.closed.countedCashCents)}
              {data.closed.differenceCents !== 0 && ` · ${data.closed.differenceCents > 0 ? 'over' : 'tekort'} ${money(Math.abs(data.closed.differenceCents))}`}
            </p>
            {data.closed.note && <p className="text-ink" data-id="element-2327" data-mp="lr4b83q2m7nv">“{data.closed.note}”</p>}
          </div> : canClose && <CloseForm data={data} onClosed={onClosed} data-id="element-2328" data-mp="qo4iuj2kjih3" />}
      </div>
    </Panel>;
}

/**
 * Kuren die vandaag met pin of contant zijn terugbetaald (vanuit het klantdossier). Ze zitten al
 * verrekend in Pin en Contant hierboven, en dus in wat er in de la hoort.
 */
function KuurRefunds({
  refunds
}: {
  refunds: KuurStop[];
}) {
  const {
    site
  } = useAdmin();
  const print = async (k: KuurStop) => {
    if (!(await printKuurCreditNote(k, site.salon))) toast.error('Creditnota openen lukte niet', {
      description: 'Probeer het opnieuw of gebruik een andere browser.'
    });
  };
  return <div className="mt-4 border-t border-line pt-4" data-id="element-2329" data-mp="2ozee03on0kll">
      <p className="text-[13px] text-ink" data-id="element-2330" data-mp="2p0z9bvis07ki">Kuur terugbetaald</p>
      <p className="text-[12px] text-muted" data-id="element-2331" data-mp="33uknx1qut3k7">Zit al in Pin en Contant hierboven.</p>
      <ul className="mt-2 space-y-1.5 text-[13px]" data-id="element-2332" data-mp="2ywj063ihgg2e">
        {refunds.map(k => <li key={k.id} className="flex items-baseline justify-between gap-3" data-id="element-2333" data-mp="19hcnmuk3zzr6">
            <span className="min-w-0" data-id="element-2334" data-mp="14irnsw8rri39">
              <span className="block truncate text-ink" data-id="element-2335" data-mp="14jt69ls476v0">{k.customerName}</span>
              <span className="block text-[12px] text-muted" data-id="element-2336" data-mp="1u7f8xgzgmp1m">
                {clock(k.at)} · {refundMethodLabel[k.method]} ·{' '}
                <button type="button" onClick={() => void print(k)} className="underline underline-offset-2 hover:text-ink" data-id="element-2337" data-mp="1y90q3ej11phm">
                  {k.number}
                </button>
              </span>
            </span>
            <span className="shrink-0 tabular-nums text-ink" data-id="element-2338" data-mp="287zmnyqipuo9">− {money(k.refundCents)}</span>
          </li>)}
      </ul>
    </div>;
}

/** Retouren die vandaag met pin of contant zijn terugbetaald (de bon kan van een eerdere dag zijn). */
function Returns({
  returns
}: {
  returns: SaleReturn[];
}) {
  const {
    site
  } = useAdmin();
  const print = async (r: SaleReturn) => {
    if (!(await printReturnCreditNote(r, site.salon))) toast.error('Creditnota openen lukte niet', {
      description: 'Probeer het opnieuw of gebruik een andere browser.'
    });
  };
  return <div className="mt-4 border-t border-line pt-4" data-id="element-2339" data-mp="30wf2uoldyj16">
      <p className="text-[13px] text-ink" data-id="element-2340" data-mp="30szo5mvmjc0a">Retour</p>
      <p className="text-[12px] text-muted" data-id="element-2341" data-mp="p8s13srjettr">Zit al in Pin en Contant hierboven.</p>
      <ul className="mt-2 space-y-1.5 text-[13px]" data-id="element-2342" data-mp="k8nssq0s95em">
        {returns.map(r => <li key={r.id} className="flex items-baseline justify-between gap-3" data-id="element-2343" data-mp="f9aw7dk40jyu">
            <span className="min-w-0" data-id="element-2344" data-mp="aara9d5in86h">
              <span className="block truncate text-ink" data-id="element-2345" data-mp="absvubk5rd2g">{r.customerName}</span>
              <span className="block text-[12px] text-muted" data-id="element-2346" data-mp="17z1bhpxh9rwj">
                {clock(r.at)} · {refundMethodLabel[r.method]} · bon {r.saleNumber} ·{' '}
                <button type="button" onClick={() => void print(r)} className="underline underline-offset-2 hover:text-ink" data-id="element-2347" data-mp="1306tgq7c47dh">
                  {r.number}
                </button>
              </span>
            </span>
            <span className="shrink-0 tabular-nums text-ink" data-id="element-2348" data-mp="t7rc9v3v3mtn">− {money(r.refundCents)}</span>
          </li>)}
      </ul>
    </div>;
}
function Row({
  label,
  value,
  small = false
}: {
  label: string;
  value: number;
  small?: boolean;
}) {
  return <div className="flex justify-between gap-3" data-id="element-2349" data-mp="u9n4j6oyroth">
      <dt className="text-muted" data-id="element-2350" data-mp="z1enow5sis9l">{label}</dt>
      <dd className={`tabular-nums ${small ? 'text-muted' : 'text-ink'}`} data-id="element-2351" data-mp="z2g9nvocgz63">{money(value)}</dd>
    </div>;
}

/**
 * Tel de la, vul het in; bij een verschil schrijf je kort op wat er gebeurde. Daarna kunnen er geen
 * bonnen meer bij. `blind` (collega): zonder het verwachte bedrag; de server rekent het verschil en
 * Sarah ziet het in haar dagoverzicht.
 */
function CloseForm({
  data,
  onClosed,
  blind = false
}: Omit<Props, 'totalsHidden'> & {
  blind?: boolean;
}) {
  const id = useId();
  const {
    site
  } = useAdmin();
  const expected = data.summary.expectedCashCents;
  const [counted, setCounted] = useState<number | undefined>(undefined);
  const [note, setNote] = useState('');
  const [touched, setTouched] = useState(false);
  // Twee stappen: eerst tellen, dan bevestigen (waarschuwingen lezen en "afsluiten" intikken).
  const [confirming, setConfirming] = useState(false);
  const [word, setWord] = useState('');
  const {
    submit,
    submitting,
    error
  } = useSubmit(closeKassaDay);
  const request = useRef(newRequestId());
  const problem = blind ? counted === undefined ? 'Vul in wat er in de la zit.' : null : closeProblem(counted, expected, note);
  const difference = blind || counted === undefined ? null : counted - expected;
  const isToday = data.day === today();
  const warnings = (() => {
    const now = new Date();
    return closeWarnings({
      toSettle: data.toSettle,
      isToday,
      nowMinutes: now.getHours() * 60 + now.getMinutes(),
      closesAt: salonDayFor(site.openingHours, site.salonExceptions, data.day)?.close ?? null
    });
  })();
  const review = () => {
    setTouched(true);
    if (problem || counted === undefined) return;
    setConfirming(true);
  };
  const close = async () => {
    if (problem || counted === undefined || !isCloseWord(word)) return;
    const day = await submit(data.day, {
      requestId: request.current,
      countedCashCents: counted,
      note: note.trim(),
      confirmWord: word.trim(),
      acknowledged: warnings.map(w => w.code)
    });
    if (!day) return;
    toast.success('Dag afgesloten', {
      description: 'Deze dag neemt geen bonnen meer aan.'
    });
    onClosed(day);
  };
  if (confirming && counted !== undefined) {
    return <div className="space-y-3" data-id="element-2352" data-mp="8rxz7plp681u">
        <p className="text-[13px] text-ink" data-id="element-2353" data-mp="8v29n4pra00y">
          {isToday ? 'Vandaag' : dayLong(data.day)} definitief afsluiten
        </p>
        <p className="text-[13px] text-muted" data-id="element-2354" data-mp="3v7sg7isdsoxu">
          Geteld {money(counted)}
          {difference ? ` · ${difference > 0 ? 'over' : 'tekort'} ${money(Math.abs(difference))}` : ''}. Daarna kunnen er geen bonnen of correcties meer bij, en dat
          is niet terug te draaien.
        </p>
        {warnings.length > 0 && <ul role="list" aria-label="Let op" className="space-y-1.5 rounded-md bg-sand px-3 py-2.5 text-[13px] text-ink" data-id="element-2355" data-mp="3h77x0lik0r3t">
            {warnings.map(w => <li key={w.code} className="flex gap-2" data-id="element-2356" data-mp="1rrnpzhpbixge">
                <AlertTriangleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-clay" strokeWidth={1.75} aria-hidden="true" data-id="element-2357" data-mp="1rr49m5fbgh4h" />
                {w.text}
              </li>)}
          </ul>}
        <label htmlFor={`${id}-word`} className="block" data-id="element-2358" data-mp="20svghdlletzx">
          <span className="mb-1 block text-[12px] text-muted" data-id="element-2359" data-mp="25rf21cw9y3rr">
            Typ <span className="text-ink" data-id="element-2360" data-mp="190wcq527mmwq">{CLOSE_WORD}</span> om te bevestigen{warnings.length > 0 ? ' dat je dit gezien hebt' : ''}
          </span>
          <input id={`${id}-word`} type="text" autoComplete="off" autoCapitalize="off" spellCheck={false} value={word} onChange={e => setWord(e.target.value)} className={`${inputCls} max-w-[12rem]`} data-id="element-2361" data-mp="1d3asggal16js" />
        </label>
        {error && <p role="alert" className="text-[12px] text-clay" data-id="element-2362" data-mp="pb2kxqzhw7si">
            {error}
          </p>}
        <div className="flex flex-col gap-2 sm:flex-row" data-id="element-2363" data-mp="1xmxfy75yvbrv">
          <button type="button" onClick={() => void close()} disabled={submitting || !isCloseWord(word)} aria-busy={submitting} className={`${btnPrimary} flex-1 disabled:opacity-50`} data-id="element-2364" data-mp="1xlco4pb1e6xp">
            {submitting ? 'Afsluiten…' : 'Definitief afsluiten'}
          </button>
          <button type="button" onClick={() => setConfirming(false)} disabled={submitting} className={`${btnSecondary} flex-1`} data-id="element-2365" data-mp="1eo1pn6x8bhjb">
            Terug
          </button>
        </div>
      </div>;
  }
  return <div className="space-y-3" data-id="element-2366" data-mp="1nohr6auewelj">
      <p className="text-[13px] text-ink" data-id="element-2367" data-mp="1npsvcbrrrod6">
        Dag afsluiten {blind ? <span className="text-muted" data-id="element-2368" data-mp="1ipwmnz84n8ra">· tel wat er contant in de la zit</span> : <span className="text-muted" data-id="element-2369" data-mp="1irr7ldjef0vd">· contant verwacht {money(expected)}</span>}
      </p>
      {!isToday && <p className="text-[12px] text-muted" data-id="element-2370" data-mp="2u4ts2jomcakt">
          Je sluit een eerdere dag af. Tel alleen het geld van die dag: trek eraf wat er vandaag al contant binnenkwam.
        </p>}
      <label className="block" data-id="element-2371" data-mp="32o6tdph7zcfb">
        <span className="mb-1 block text-[12px] text-muted" data-id="element-2372" data-mp="36nxgnj7jfaf3">Geteld in de la (€, zonder wisselgeldvoorraad)</span>
        <NumberInput decimals allowEmpty value={counted === undefined ? undefined : counted / 100} onChange={v => setCounted(v === undefined ? undefined : Math.round(v * 100))} className="w-36" data-id="element-2373" data-mp="2sofulblmmb9f" />
      </label>
      {difference !== null && difference !== 0 && <p className="text-[13px] text-clay" aria-live="polite" data-id="element-2374" data-mp="3qbewq1v38bep">
          {difference > 0 ? 'Over' : 'Tekort'}: {money(Math.abs(difference))}
        </p>}
      <label htmlFor={`${id}-note`} className="block" data-id="element-2375" data-mp="3blz7wrf4ag1n">
        <span className="mb-1 block text-[12px] text-muted" data-id="element-2376" data-mp="3gl22fhxh13vd">{blind ? 'Notitie voor Sarah (optioneel)' : `Notitie ${difference ? '(verplicht bij een verschil)' : '(optioneel)'}`}</span>
        <AutoTextarea id={`${id}-note`} value={note} onChange={e => setNote(e.target.value)} minRows={2} maxLength={300} className={inputCls} data-id="element-2377" data-mp="cwidi98915su" />
      </label>
      {(touched && problem || error) && <p role="alert" className="text-[12px] text-clay" data-id="element-2378" data-mp="2geq651qd7z28">
          {error ?? problem}
        </p>}
      <button type="button" onClick={review} className={`${btnPrimary} w-full`} data-id="element-2379" data-mp="2bevg10eezdi8">
        Dag afsluiten
      </button>
    </div>;
}